/*
 * DO NOT DELETE — base-info and page-design tags are consumed by project-snapshot tooling for quick page overview. Always update them to reflect actual page content.
 * <base-info>
 * Description: 用户登录或通过邀请码注册的入口页面
 * Style referenceFiles:
 * Design for: Mobile
 * </base-info>
 * <page-design>
 * ## Features & Interactions
 * - 两种模式：登录（手机号+密码）与注册（手机号+密码+确认密码+邀请码）
 * - 底部链接在两种模式之间切换
 * - 登录/注册成功后跳转 -> Settings（云端备份入口）
 * - 「暂不登录」链接返回首页（继续本地模式）
 *
 * ## Basic Layout
 * Full-screen centered form, dark bg (#12141D)
 *
 * ## Reuse Components
 * 无
 *
 * ## Reference
 * - canvas/[Feature] Auth/feature-brief.md
 * </page-design>
 */

import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { pageLinks } from '../../pageLinks'
import * as LoginStore from '../../stores/LoginStore'
import { toast } from '@/components/function/base/Toaster'
import { FormError } from '@/lib/apiFetch'

function Login() {
  const [mode, setMode] = useAtom(LoginStore.modeAtom)
  const [phone, setPhone] = useAtom(LoginStore.phoneAtom)
  const [password, setPassword] = useAtom(LoginStore.passwordAtom)
  const [confirmPassword, setConfirmPassword] = useAtom(LoginStore.confirmPasswordAtom)
  const [inviteCode, setInviteCode] = useAtom(LoginStore.inviteCodeAtom)
  const isSubmitting = useAtomValue(LoginStore.isSubmittingAtom)
  const submit = useSetAtom(LoginStore.submitAtom)
  const register = useSetAtom(LoginStore.registerAtom)
  const navigate = useNavigate()

  const isRegister = mode === 'register'

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    void (async () => {
      try {
        if (isRegister) {
          await register()
        } else {
          await submit()
        }
        // Login is reached from the Settings "enable cloud backup" entry, so
        // return there to show the just-enabled sync controls.
        void navigate(pageLinks.Settings())
      } catch (err) {
        setPassword('')
        if (isRegister) setConfirmPassword('')
        if (err instanceof FormError) {
          toast.error(err.message)
        } else {
          toast.error(isRegister ? '注册失败，请检查网络' : '登录失败，请检查网络')
        }
      }
    })()
  }

  const switchMode = () => {
    setPassword('')
    setConfirmPassword('')
    setInviteCode('')
    setMode(isRegister ? 'login' : 'register')
  }

  return (
    <div
      className="flex flex-col h-screen px-6 justify-center"
      style={{ backgroundColor: '#12141D' }}
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col gap-8 w-full max-w-sm mx-auto"
      >
        {/* Brand Logo Area */}
        <div className="flex flex-col items-center gap-3">
          <img src="/logo.png" alt="黑洞健身" className="w-20 h-20 rounded-2xl" />
          <h1
            className="text-3xl font-extrabold tracking-tight"
            style={{ color: '#F9FAFB' }}
          >
            黑洞健身
          </h1>
          {isRegister && (
            <p className="text-sm text-gray-400">使用邀请码注册新账号</p>
          )}
        </div>

        {/* Form Inputs */}
        <div className="flex flex-col gap-4">
          {/* Phone Number Input */}
          <div className="relative">
            <div
              className="absolute left-4 top-1/2 -translate-y-1/2"
              style={{ color: '#6B7280' }}
            >
              <Icon icon="mdi:phone-outline" style={{ fontSize: '20px' }} />
            </div>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="请输入手机号"
              className="w-full pl-12 pr-4 py-4 rounded-xl text-base font-medium outline-none placeholder:font-normal"
              style={{
                backgroundColor: '#1E2029',
                color: '#F9FAFB',
                border: '1.5px solid #2A2D3A',
              }}
              autoComplete="tel"
            />
          </div>

          {/* Password Input */}
          <div className="relative">
            <div
              className="absolute left-4 top-1/2 -translate-y-1/2"
              style={{ color: '#6B7280' }}
            >
              <Icon icon="mdi:lock-outline" style={{ fontSize: '20px' }} />
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isRegister ? '请输入密码（至少 6 位）' : '请输入密码'}
              className="w-full pl-12 pr-4 py-4 rounded-xl text-base font-medium outline-none placeholder:font-normal"
              style={{
                backgroundColor: '#1E2029',
                color: '#F9FAFB',
                border: '1.5px solid #2A2D3A',
              }}
              autoComplete={isRegister ? 'new-password' : 'current-password'}
            />
          </div>

          {/* Register-only: confirm password */}
          {isRegister && (
            <div className="relative">
              <div
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: '#6B7280' }}
              >
                <Icon icon="mdi:lock-check-outline" style={{ fontSize: '20px' }} />
              </div>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="请再次输入密码"
                className="w-full pl-12 pr-4 py-4 rounded-xl text-base font-medium outline-none placeholder:font-normal"
                style={{
                  backgroundColor: '#1E2029',
                  color: '#F9FAFB',
                  border: '1.5px solid #2A2D3A',
                }}
                autoComplete="new-password"
              />
            </div>
          )}

          {/* Register-only: invite code */}
          {isRegister && (
            <div className="relative">
              <div
                className="absolute left-4 top-1/2 -translate-y-1/2"
                style={{ color: '#6B7280' }}
              >
                <Icon icon="mdi:ticket-confirmation-outline" style={{ fontSize: '20px' }} />
              </div>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="请输入邀请码"
                className="w-full pl-12 pr-4 py-4 rounded-xl text-base font-medium outline-none placeholder:font-normal tracking-widest"
                style={{
                  backgroundColor: '#1E2029',
                  color: '#F9FAFB',
                  border: '1.5px solid #2A2D3A',
                }}
                autoComplete="off"
                maxLength={8}
              />
            </div>
          )}
        </div>

        {/* Primary Button */}
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full py-4 rounded-xl text-white text-lg font-bold tracking-wide transition-opacity disabled:opacity-60"
          style={{
            backgroundColor: '#10B981',
            borderBottom: '4px solid #059669',
          }}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center gap-2">
              <Icon
                icon="mdi:loading"
                className="animate-spin"
                style={{ fontSize: '20px' }}
              />
              {isRegister ? '注册中...' : '登录中...'}
            </span>
          ) : (
            isRegister ? '注 册' : '登 录'
          )}
        </button>

        {/* Mode switch */}
        <div className="text-center text-sm text-gray-400">
          {isRegister ? '已有账号？' : '还没有账号？'}
          <button
            type="button"
            onClick={switchMode}
            className="ml-1 text-emerald-400 font-bold"
          >
            {isRegister ? '返回登录' : '使用邀请码注册'}
          </button>
        </div>

        {/* Guest escape hatch */}
        <div className="text-center">
          <button
            type="button"
            onClick={() => navigate(pageLinks.Home())}
            className="text-sm text-gray-500 underline"
          >
            暂不登录，继续本地使用
          </button>
        </div>
      </form>
    </div>
  )
}

export default Login
